import '../../theme/datepicker.css'

import styled from '@emotion/styled'
import { Checkbox } from 'app/components/Common/Checkbox'
import { FormMessages } from 'app/components/Common/FormMessages'
import { Input } from 'app/components/Common/Input'
import { Line } from 'app/components/Common/Line'
import { Select } from 'app/components/Common/Select'
import { Spinner } from 'app/components/Common/Spinner'
import { Textarea } from 'app/components/Common/Textarea'
import { IconLogo } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import {
  FormData,
  QuotationFormSenderBackend,
} from 'app/utils/QuotationFormSender'
import { useVocabularyData } from 'app/utils/vocabulary'
import { de, enUS, it } from 'date-fns/esm/locale'
import { rgba } from 'emotion-rgba'
import { isLeft } from 'fp-ts/lib/These'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { useForm } from 'react-hook-form'
import NumericInput from 'react-numeric-input'

registerLocale('de', de)
registerLocale('en', enUS)
registerLocale('it', it)

const EMAIL_REGEX = /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i

export interface Props {
  description?: string
  label?: string
  languageCode: string
  languagePrefix?: string | null
  onQuotationFormSendToBackend: QuotationFormSenderBackend
  title?: string
}

export const QuotationForm = memo(function QuotationForm({
  description,
  label,
  languageCode,
  languagePrefix,
  onQuotationFormSendToBackend,
  title,
}: Props) {
  /*
   * Datepicker configuration
   */
  const [startDate, setStartDate] = useState<any>()
  const [startValue, setStartValue] = useState('')
  const [endDate, setEndDate] = useState<Date>()
  const [endValue, setEndValue] = useState('')

  /*
   * Form data
   */
  const [child1Age, setChild1Age] = useState(0)
  const [child2Age, setChild2Age] = useState(0)
  const [child3Age, setChild3Age] = useState(0)
  const [child4Age, setChild4Age] = useState(0)
  const yearsLabel = useVocabularyData('years', languageCode)

  const [isSendingForm, setIsSendingForm] = useState(false)
  const [displaySendErrorFeedback, setDisplaySendErrorFeedback] =
    useState(false)
  const [displaySendConfirmFeedback, setDisplaySendConfirmFeedback] =
    useState(false)

  const onValidSubmission = useCallback(
    async (data: FormData) => {
      if (isSendingForm) {
        return
      }

      setDisplaySendErrorFeedback(false)
      setDisplaySendConfirmFeedback(false)
      setIsSendingForm(true)
      const sendResult = await onQuotationFormSendToBackend(data)
      setIsSendingForm(false)

      if (isLeft(sendResult)) {
        setDisplaySendErrorFeedback(true)
      } else {
        setDisplaySendConfirmFeedback(true)

        typeof window.gtag !== 'undefined' &&
          window.gtag('event', 'Submit', {
            event_category: 'Website',
            event_label: 'Quotation Form',
          })
      }
    },
    [isSendingForm, onQuotationFormSendToBackend],
  )

  const { register, formState, handleSubmit, watch, setValue } =
    useForm<FormData>({
      mode: 'onBlur',
      shouldFocusError: true,
      defaultValues: {
        offer_title:
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('offer') ||
              undefined
            : undefined,
        room_title:
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('room') ||
              undefined
            : undefined,
      },
    })

  const onSubmit = useMemo(
    () => handleSubmit(onValidSubmission),
    [handleSubmit, onValidSubmission],
  )

  /**
   * Handle scrolling to first field with errors
   */

  const childrenValue = watch('children') || ''

  useEffect(() => {
    if (formState.errors) {
      const firstErrorElement = Object.values(formState.errors).find(
        (fieldError) => fieldError?.ref !== undefined,
      )?.ref as HTMLElement | undefined

      if (firstErrorElement && firstErrorElement.scrollIntoView) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }

    var ages = ''

    switch (childrenValue) {
      case '1':
        ages = `${child1Age} ${yearsLabel}`
        break
      case '2':
        ages = `${child1Age}, ${child2Age} ${yearsLabel}`
        break
      case '3':
        ages = `${child1Age}, ${child2Age}, ${child3Age} ${yearsLabel}`
        break
      case '4':
        ages = `${child1Age}, ${child2Age}, ${child3Age}, ${child4Age} ${yearsLabel}`
        break
    }

    register('ages', {
      value: ages,
    })

    if (startValue) {
      setValue('arrival', startValue)
    }

    if (endValue) {
      setValue('departure', endValue)
    }
  }, [
    formState.errors,
    childrenValue,
    child1Age,
    child2Age,
    child3Age,
    child4Age,
    startValue,
    endValue,
  ])

  return (
    <Container>
      <IconLogo />

      {label ? <Claim>{label}</Claim> : null}
      {title ? (
        <Title>
          {title}
          <Line />
        </Title>
      ) : null}
      {description ? (
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      ) : null}

      <Form
        onSubmit={isSendingForm ? undefined : onSubmit}
        noValidate
        autoComplete={'off'}
      >
        <Spinner className={isSendingForm ? '' : 'hidden'} />
        <Wrapper row wrap space="between">
          <Category>
            {useVocabularyData('personal-data', languageCode)}
          </Category>
          <Field>
            <Input
              error={formState.errors.name !== undefined}
              isDirty={formState.dirtyFields.name}
              placeholder={`${useVocabularyData('name', languageCode)}*`}
              type="text"
              required={true}
              {...register('name', {
                required: true,
                validate: (name) => name.length > 2,
              })}
            />
          </Field>
          <Field>
            <Input
              error={formState.errors.lastname !== undefined}
              isDirty={formState.dirtyFields.lastname}
              placeholder={`${useVocabularyData('lastname', languageCode)}*`}
              type="text"
              required={true}
              {...register('lastname', {
                required: true,
                validate: (lastname) => lastname.length > 2,
              })}
            />
          </Field>
          <Field>
            <Input
              error={formState.errors.email !== undefined}
              isDirty={formState.dirtyFields.email}
              placeholder={`${useVocabularyData('email', languageCode)}*`}
              type="email"
              required={true}
              {...register('email', {
                required: true,
                validate: (email) => EMAIL_REGEX.test(email),
              })}
            />
          </Field>
          <Field>
            <Input
              type="text"
              placeholder={useVocabularyData('telephone', languageCode)}
              {...register('phone')}
            />
          </Field>
          <Field className="large-field">
            <Input
              type="text"
              placeholder={useVocabularyData('nationality', languageCode)}
              {...register('nationality')}
            />
          </Field>
        </Wrapper>

        <Wrapper row wrap space="between">
          <Category>{useVocabularyData('stay-data', languageCode)}</Category>
          <Field>
            <DatePicker
              className={
                formState.errors.arrival !== undefined ? 'error' : undefined
              }
              dateFormat="dd/MM/yyyy"
              disabledKeyboardNavigation={true}
              locale={languagePrefix || 'it'}
              {...register('arrival', {
                required: true,
              })}
              onChange={(date) => {
                if (date) {
                  setStartDate(date)
                  setStartValue(
                    date.toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: '2-digit',
                      year: 'numeric',
                    }),
                  )
                  setEndDate(undefined)
                }
              }}
              onFocus={(e) => (e.target.readOnly = true)}
              placeholderText={`${useVocabularyData(
                'check-in-date',
                languageCode,
              )}*`}
              minDate={new Date()}
              required={true}
              selected={startDate}
              withPortal={
                typeof window !== 'undefined' && window.innerWidth > 767
                  ? false
                  : true
              }
            />
          </Field>
          <Field>
            <DatePicker
              className={
                formState.errors.departure !== undefined ? 'error' : undefined
              }
              dateFormat="dd/MM/yyyy"
              disabledKeyboardNavigation={true}
              locale={languagePrefix || 'it'}
              {...register('departure', {
                required: true,
              })}
              onChange={(date) => {
                if (date) {
                  setEndDate(date)
                  setEndValue(
                    date.toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: '2-digit',
                      year: 'numeric',
                    }),
                  )
                }
              }}
              onFocus={(e) => (e.target.readOnly = true)}
              placeholderText={`${useVocabularyData(
                'check-out-date',
                languageCode,
              )}*`}
              minDate={new Date(startDate?.getTime() + 1 * 24 * 60 * 60 * 1000)}
              required={true}
              selected={endDate}
              withPortal={
                typeof window !== 'undefined' && window.innerWidth > 767
                  ? false
                  : true
              }
            />
          </Field>
          <Field>
            <Select
              error={formState.errors.adults !== undefined}
              isDirty={formState.dirtyFields.adults}
              options={[
                {
                  value: '',
                  label: `${useVocabularyData('adults', languageCode)} *`,
                  hidden: true,
                },
                { value: '1' },
                { value: '2' },
                { value: '3' },
                { value: '4' },
              ]}
              required={true}
              {...register('adults', {
                required: true,
                validate: (adults) => adults != '',
              })}
            />
          </Field>
          <Field>
            <Select
              options={[
                {
                  value: '',
                  label: useVocabularyData('children', languageCode),
                  hidden: true,
                },
                { value: '0' },
                { value: '1' },
                { value: '2' },
                { value: '3' },
                { value: '4' },
              ]}
              {...register('children')}
            />
          </Field>
          <ChildsAges dial={3} row wrap>
            <Age className={childrenValue > '0' ? 'visible' : undefined}>
              <Label>{`${useVocabularyData(
                'child-age',
                languageCode,
              )} 1`}</Label>
              <NumericInput
                min={0}
                max={12}
                style={false}
                value={child1Age}
                onChange={(num: any) => {
                  setChild1Age(num)
                }}
              />
            </Age>
            <Age className={childrenValue > '1' ? 'visible' : undefined}>
              <Label>{`${useVocabularyData(
                'child-age',
                languageCode,
              )} 2`}</Label>
              <NumericInput
                min={0}
                max={12}
                style={false}
                value={child2Age}
                onChange={(num: any) => {
                  setChild2Age(num)
                }}
              />
            </Age>
            <Age className={childrenValue > '2' ? 'visible' : undefined}>
              <Label>{`${useVocabularyData(
                'child-age',
                languageCode,
              )} 3`}</Label>
              <NumericInput
                min={0}
                max={12}
                style={false}
                value={child3Age}
                onChange={(num: any) => {
                  setChild3Age(num)
                }}
              />
            </Age>
            <Age className={childrenValue > '3' ? 'visible' : undefined}>
              <Label>{`${useVocabularyData(
                'child-age',
                languageCode,
              )} 4`}</Label>
              <NumericInput
                min={0}
                max={12}
                style={false}
                value={child4Age}
                onChange={(num: any) => {
                  setChild4Age(num)
                }}
              />
            </Age>
          </ChildsAges>
          <Field className="large-field">
            <Textarea
              placeholder={useVocabularyData('message', languageCode)}
              required={false}
              {...register('message')}
            />
          </Field>
          <Input type="hidden" {...register('offer_title')} />
          <Input type="hidden" {...register('room_title')} />
          <Checkbox
            error={formState.errors.privacy_policy !== undefined}
            label={useVocabularyData('form-policy-text', languageCode)}
            required={true}
            {...register('privacy_policy', {
              required: true,
            })}
          />
          <Input
            name="submit"
            type="submit"
            value={useVocabularyData('send-request', languageCode)}
            variant="submit"
          />
        </Wrapper>
        {displaySendErrorFeedback ? (
          <FormMessages
            text={useVocabularyData('form-error-message', languageCode)}
            title={useVocabularyData('form-error-message-title', languageCode)}
            type="error"
          />
        ) : null}
        {displaySendConfirmFeedback ? (
          <FormMessages
            text={useVocabularyData('form-confirmation-message', languageCode)}
            title={useVocabularyData(
              'form-confirmation-message-title',
              languageCode,
            )}
          />
        ) : null}
      </Form>
    </Container>
  )
})

const Container = styled.section`
  max-width: 84.125rem;
  margin: 9.25rem auto 12.5rem;
  padding: 0 10.556vw;
  text-align: center;

  > svg {
    width: 4.9375rem;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralDark2};
    margin-bottom: 3.25rem;
  }

  @media (max-width: 1199px) {
    max-width: 60.6875rem;
    margin: 8.125rem auto 7.5rem;
    padding: 0 1.875rem;

    > svg {
      width: 3.75rem;
      margin-bottom: 2.25rem;
    }
  }

  @media (max-width: 767px) {
    margin-bottom: 0;
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
`

const Claim = styled.h1`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.25rem;
  margin-bottom: 0.9375rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1rem;
  }
`

const Title = styled.h2`
  max-width: 28.125rem;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3rem;
  font-weight: 500;
  line-height: 4rem;
  margin: auto;

  > div {
    margin-right: auto;
    margin-left: auto;
  }

  @media (max-width: 1199px) {
    font-size: 2.25rem;
    line-height: 2.875rem;

    > div {
      margin-top: 1.25rem;
    }
  }
`

const Description = styled.div`
  max-width: 45.625rem;
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  line-height: 1.875rem;
  margin: 2.375rem auto 0;

  p {
    margin: 1em 0;
  }

  @media (max-width: 1199px) {
    font-size: 1rem;
    margin-top: 2.25rem;
  }
`

const Form = styled.form`
  margin-top: 4.125rem;
  position: relative;
  text-align: left;
  z-index: 2;
`

const Wrapper = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  margin-top: 0.25rem;
  padding: 3.125rem 6.736vw 3.75rem;

  .react-datepicker {
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    background-color: ${({ theme }) => theme.colors.variants.neutralDark2};
    color: ${({ theme }) => theme.colors.variants.neutralLight4};
  }

  .react-datepicker__portal {
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${rgba(theme.colors.variants.neutralDark1, 0.7)};
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
  }

  .react-datepicker__current-month {
    font-family: ${({ theme }) => theme.fontFamily.heading};
  }

  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day:hover,
  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.variants.primaryLight};
    color: ${({ theme }) => theme.colors.variants.neutralDark2};
  }

  .react-datepicker__day:focus {
    background-color: ${({ theme }) =>
      theme.colors.variants.primaryLight} !important;
    color: ${({ theme }) => theme.colors.variants.neutralDark2} !important;
  }

  .react-datepicker__day--today {
    &:after {
      background: ${({ theme }) => theme.colors.variants.primaryLight};
    }
  }

  .react-datepicker__day--disabled {
    color: ${({ theme }) => theme.colors.variants.secondaryLight} !important;
  }

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    height: 100%;
    position: relative;
    bottom: auto;
    left: auto;
    input {
      width: 100%;
      height: 3.75rem;
      background: url('/calendar.svg') no-repeat right center;
      border-bottom: 0.125rem solid
        ${rgba(theme.colors.variants.neutralDark2, 0.3)};
      color: ${({ theme }) => theme.colors.variants.neutralDark4};
      cursor: pointer;
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 0.9375rem;
      font-weight: 500;
      &.error {
        border-color: ${({ theme }) => theme.colors.variants.dangerLight};
      }

      &::-webkit-input-placeholder {
        color: ${theme.colors.variants.neutralDark2};
        opacity: 1;
      }
      &::-moz-placeholder {
        color: ${theme.colors.variants.neutralDark2};
        opacity: 1;
      }
      &::-ms-input-placeholder {
        color: ${theme.colors.variants.neutralDark2};
        opacity: 1;
      }
      &::placeholder {
        color: ${theme.colors.variants.neutralDark2};
        opacity: 1;
      }
    }
  }

  @media (max-width: 767px) {
    margin-right: -1.25rem;
    margin-left: -1.25rem;
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
`

const Category = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.875rem;
  margin-bottom: -0.625rem;
`

const Field = styled.div`
  width: calc(50% - 1.875rem);
  margin-top: 1.875rem;
  &.large-field {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: calc(50% - 0.625rem);
  }
`

const ChildsAges = styled(FlexBox)`
  width: calc(100% + 3.125rem);
  margin-left: -3.125rem;

  .react-numeric-input {
    display: flex;
    align-items: center;
    border-bottom: 0.125rem solid
      ${rgba(theme.colors.variants.neutralDark2, 0.3)};
    position: relative;

    input {
      width: 2.5rem;
      height: 2.5rem;
      caret-color: transparent;
      color: ${({ theme }) => theme.colors.variants.neutralDark2};
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 0.9375rem;
      font-weight: 500;
      line-height: 1.25rem;
      margin-right: auto;
      pointer-events: none;
    }
    b {
      width: 1.25rem;
      height: 0.625rem;
      cursor: pointer;
      position: absolute;
      right: 0;
      &:before {
        content: '';
        width: 100%;
        height: 100%;
        background: url('/dropdown.svg') no-repeat center;
        background-size: 0.625rem;
        position: absolute;
        top: 0;
        left: 0;
      }
      &:nth-of-type(1) {
        top: 0.5625rem;
        &:before {
          transform: scaleY(-1);
        }
      }
      &:last-of-type {
        top: 1.3125rem;
      }
    }
  }

  @media (max-width: 767px) {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
  }
`

const Age = styled.div`
  display: none;
  width: calc(25% - 3.125rem);
  margin: 3.125rem 0 0 3.125rem;
  &.visible {
    display: block;
  }

  @media (max-width: 767px) {
    width: calc(50% - 0.625rem);
    margin-left: 0;
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.125rem;
`
